/**
 * How deep a county is, said before someone commits to it.
 *
 * The chooser and the coverage map both render *before* a subscription is
 * bought or moved, and until now both described a county by **volume** —
 * parcels and buildings — which is the number that varies least between a
 * county worth $29 and one that is mostly a parcel roll. What varies is
 * **depth**: whether the county publishes a case feed at all. Most live
 * counties do not — thirty-one of thirty-four on 15 Sep 2026 — and nothing at
 * the point of choice said so.
 *
 * Derived from `signals` on `/api/counties`, never written per county. That is
 * the same rule `scoreScope` follows and for the same reason: the masthead has
 * already gone stale once by hard-coding what a county could not do, and every
 * public-records request that lands is a county changing category. A sentence
 * computed from the served list cannot outlive its own truth.
 *
 * See `docs/county-value-audit-2026-08.md` for the measurements this exists to
 * expose, and roadmap item 6 for what it is a step toward.
 */

import { scoreScope } from "./scoreScope";

/**
 * The signals that can only come from a code-enforcement case register.
 *
 * Deliberately not "every signal that is not about the owner": permits are a
 * separate feed, and `expired_permit` / `no_recent_permit` fire off a permit
 * register rather than a case one — Broward has permits and no case signal,
 * and reading them as cases would overstate it. Measured against
 * `SIGNALS_BY_COUNTY`, this set was true of exactly six counties when written,
 * the same six that carried a `distress_rate`. Since 7 Sep 2026 Pinellas,
 * Volusia and Broward have withdrawn their frozen case signals, so it is three:
 * Miami-Dade, Hillsborough and Lee.
 */
const CASE_SIGNALS = [
  "code_enforcement",
  "unsafe_structure",
  "lien_recorded",
  "other_violation",
];

/**
 * How many signals the score knows, for "N of 14". The keys of `WEIGHTS` in
 * the engine's catalog; thirteen since 29 Sep 2026, when *SIRS assessment*
 * joined the twelve, and fourteen the same day, with *taxes unpaid* — which
 * only a county whose Tax Collector list is held declares, so no county
 * carries all fourteen.
 */
export const SIGNAL_TOTAL = 14;

export interface CountyDepth {
  /** How many signals the county publishes a feed for. */
  signals: number;
  /** Whether any code-enforcement case register is published here. */
  hasCases: boolean;
  /** Whether the parcel score can separate one row from another. */
  ranks: boolean;
  /** Two or three words for a list row. */
  badge: string;
  /** One sentence for a panel, stating what is held rather than what is not. */
  summary: string;
}

export function countyDepth(signals?: string[]): CountyDepth {
  const held = signals ?? [];
  const hasCases = CASE_SIGNALS.some((flag) => held.includes(flag));
  const { ranks } = scoreScope(signals);

  return {
    signals: held.length,
    hasCases,
    ranks,
    // "Roll only" describes what is there. "No case feed" describes what is
    // not, which reads as a complaint about the county rather than a fact
    // about the record — and the county is not at fault for what it publishes.
    badge: hasCases ? "case records" : "roll only",
    summary: summaryFor(held, hasCases, ranks),
  };
}

function summaryFor(held: string[], hasCases: boolean, ranks: boolean): string {
  const sirs = held.includes("sirs_assessment");
  // What a county without case records scores on, in the order a reader
  // meets them: the county's own roll first, then the state's and the Tax
  // Collector's records where the county carries them.
  const bases = [
    "the property roll",
    "milestone standing",
    ...(sirs ? ["the SIRS reports associations file with the state"] : []),
    ...(held.includes("tax_delinquent")
      ? ["the Tax Collector's list of unpaid property taxes"] : []),
  ];
  const rests = `${bases.slice(0, -1).join(", ")} and ${bases[bases.length - 1]}`;
  // **"published here" would be a county-wide claim, and it is never true.**
  // Broward's permit register is Fort Lauderdale's and its only case register
  // is withdrawn, Pinellas's was Clearwater's and is withdrawn too,
  // Hillsborough's covers the unincorporated county only — and even
  // Miami-Dade, the deepest, is missing every municipality's own code
  // compliance. Saying it plainly costs one clause and avoids replacing the
  // overclaim this module exists to remove with a fresh one. Which
  // municipalities is what `/api/coverage` publishes, so the sentence points
  // there rather than trying to summarise it.
  const scored = hasCases
    ? "Code enforcement records feed the score for part of this county; " +
      "the coverage map breaks it down by municipality."
    : `No code enforcement records are published here, so scoring rests on ${rests}.`;

  // The rank caveat is only added where it is true, and it is the sharper of
  // the two facts: a score that cannot separate rows is a different problem
  // from a score built on fewer inputs.
  const caveat = ranks
    ? ""
    : sirs
      ? " Scored parcels here carry the same score unless their building " +
        "reported a SIRS assessment."
      : " Every scored parcel here carries the same score.";

  return `${held.length} of ${SIGNAL_TOTAL} signals. ${scored}${caveat}`;
}
